'use client';

import { cn } from '@/lib/cn';
import { memo, type ReactNode } from 'react';

// Uses `z-[99999]` instead of the semantic `z-999` critical-overlay token so
// the drag strip sits above Radix Dialog portals (z-50..101) *and* any
// third-party / native-style overlays that park themselves in the
// single-thousands range. Dropping to z-999 re-introduces the bug that
// APP-4382 fixes if another overlay later claims z-999.
//
// `bg-surface-accent` is the Windows default — `tauri-app-header.tsx` overrides
// it on macOS with `bg-transparent md:bg-surface-accent` so the title bar
// matches the page below `md` (where the page paints a white surface) and
// merges with the sand-toned chrome at desktop widths. Windows is opaque at
// every breakpoint because its title bar IS the only chrome.
// Height reads the `--app-titlebar-height` var (2rem fallback for the
// pre-hydration paint) rather than a fixed `h-8`: on macOS the var
// counter-divides the zoom factor so the bar holds a constant physical size
// while the page zooms out — the native traffic lights don't scale with
// `set_zoom`, and a bar that shrinks under them puts page content inside the
// OS-reserved drag strip (see `TauriTitlebarHeightVar`).
export const TAURI_DRAG_SPACER_CLASSNAME =
  'pointer-events-auto relative z-[99999] flex h-[var(--app-titlebar-height,2rem)] select-none items-center justify-between bg-surface-accent px-2';

// Base title-bar height at 100% zoom. Published (divided by the zoom factor
// on macOS) as `--app-titlebar-height` by `TauriTitlebarHeightVar` so layout
// and fixed/portaled surfaces (page-shell top padding, design-system
// sheets/tooltips) can clear the bar without a Tauri check.
export const TAURI_TITLEBAR_HEIGHT_PX = 32;

type TauriDragSpacerProps = {
  children?: ReactNode;
  className?: string;
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

// Covers the top ~28px native window-drag strip that macOS reserves when
// `titleBarStyle: "Overlay"` + `decorations: true` are set in tauri.conf.json.
// Without this, pointer events in that strip are consumed by the OS and never
// reach the webview — modals (e.g. force-update) that paint over that area
// become partially unclickable.
// On Windows the native chrome is stripped at runtime (APP-4898, see
// `utils::window::initialize_window`), so this strip is also the sole
// drag affordance and host for the React-side window controls.
export const TauriDragSpacer = memo(
  ({ children, className, onContextMenu }: TauriDragSpacerProps) => (
    <div
      className={cn(TAURI_DRAG_SPACER_CLASSNAME, className)}
      data-tauri-drag-region
      onContextMenu={onContextMenu}
    >
      {children}
    </div>
  )
);

TauriDragSpacer.displayName = 'TauriDragSpacer';
